import _ from "lodash";
import { ALLOWED_ATTRIBUTE_TYPES } from "./consts";

const CART_CUTOFFF_TIME_IN_MINUTES = 20;

export const CART_CUTOFF_TIME_IN_MILLIS =
  CART_CUTOFFF_TIME_IN_MINUTES * 60 * 1000;

export const getAllProducts = (categories, products = []) => {
  categories.forEach((category) => {
    if (category?.items?.length) {
      products.push(...category.items);
    }
    if (category?.categories?.length)
      getAllProducts(category.categories, products);
  });

  return products;
};

export const formatPrice = (price) => {
  if (price) {
    try {
      const formattingOptions = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      };
      const numberFormatter = new Intl.NumberFormat("en-US", formattingOptions);
      const result = numberFormatter.format(price);
      if (result) {
        return result;
      }
      return 0;
    } catch {
      return 0;
      // ignore
    }
  }
  return 0;
};

export const getRecommendedObjectsIds = (attributeIds = [], attributes) => {
  const foundAttributes = attributes.filter(({ objectTypeAttributeId }) =>
    attributeIds.includes(objectTypeAttributeId)
  );
  return foundAttributes
    .map(({ objectAttributeValues }) => objectAttributeValues)
    .flat()
    .map(({ referencedObject: { id } }) => id);
};

export const transformCardAttributes = ({
  cardAttributes = [],
  attributes = [],
}) =>
  cardAttributes.filter(Boolean).map((cardAttributeId) => {
    const transformedAttribute = attributes.find(
      ({ objectTypeAttributeId }) => cardAttributeId === objectTypeAttributeId
    );

    const { objectAttributeValues: [{ value, displayValue } = {}] = [] } =
      transformedAttribute || {};

    return displayValue || value;
  });

export const transformAssetLayout = (
  attributes,
  layout = {},
  objectTypeAttributes
) => {
  const {
    items = [],
    nameAttributeId,
    descriptionAttributeId = [],
    priceAttributeId,
    stockAttributeId,
    addToCartButtonLabel = "Add to cart",
  } = layout;

  const [name, price, stock] = [
    nameAttributeId,
    priceAttributeId,
    stockAttributeId,
  ].map((attributeId) => {
    const attribute =
      attributes?.find(
        ({ objectTypeAttributeId }) => attributeId === objectTypeAttributeId
      ) || {};
    const { objectAttributeValues: [{ value, displayValue } = {}] = [] } =
      attribute;

    return displayValue || value;
  });

  const descriptionIds = Array.isArray(descriptionAttributeId)
    ? descriptionAttributeId
    : [descriptionAttributeId];

  const description = descriptionIds
    .filter(Boolean)
    .map((attributeId) => {
      const attribute =
        attributes?.find(
          ({ objectTypeAttributeId }) => attributeId === objectTypeAttributeId
        ) || {};

      const attributeName = objectTypeAttributes.find(
        ({ id }) => id === attributeId
      )?.name;

      const { objectAttributeValues: [{ value, displayValue } = {}] = [] } =
        attribute;

      return { label: attributeName, value: displayValue || value };
    })
    .filter(Boolean);

  const otherItems = items
    ?.sort((a, b) => a?.order || 0 - b?.order || 0)
    .map(({ attributeId, ...rest }) => {
      const {
        objectAttributeValues: attributeValues,
        objectTypeAttributeId: attributeTypeId,
      } =
        attributes?.find(
          ({ objectTypeAttributeId }) => objectTypeAttributeId === attributeId
        ) || {};

      if (!rest.name || !rest.label) {
        const attributeName = objectTypeAttributes.find(
          ({ id }) => attributeId === id
        )?.name;

        return {
          name: attributeName,
          attributeValues,
          attributeTypeId,
        };
      }

      return {
        ...rest,
        attributeValues,
        attributeTypeId,
      };
    });
  return {
    ...layout,
    addToCartButtonLabel,
    name,
    description,
    stock,
    price,
    formattedPrice: formatPrice(price),
    items: otherItems,
  };
};

export const transformAsset = (
  {
    hasAvatar,
    avatar: { url144: avatarUrl, mediaClientConfig },
    id,
    label,
    name,
    attributes,
  },
  layout,
  cardAttributes,
  objectTypeAttributes
) => ({
  id,
  label,
  mediaClientConfig,
  name,
  hasAvatar,
  avatarUrl,
  attributes,
  cardAttributes: transformCardAttributes({
    cardAttributes,
    attributes,
  }),
  layout: transformAssetLayout(attributes, layout, objectTypeAttributes),
});

const getAttributeValues = (attributeId, attributes) => {
  const availableAttribute = attributes.find(
    ({ objectTypeAttributeId }) => attributeId === objectTypeAttributeId
  );
  return availableAttribute?.objectAttributeValues?.map(
    ({ displayValue, value }) => displayValue || value
  );
};

export const buildFiltersFromAssetData = (
  cardAttributes,
  layoutAttributes,
  filterableAttributes = [],
  objectTypeAttributes,
  assetsValues,
  categoryName,
  showStockFilter
) => {
  const cardAttributesIds = [...(cardAttributes || [])];
  const layoutAttributesIds =
    layoutAttributes?.items?.map(({ attributeId }) => attributeId) || [];

  const allFilterAttributeIds = Array.from(
    new Set([...cardAttributesIds, ...layoutAttributesIds])
  ).filter((existingId) => !!existingId);

  const filters = allFilterAttributeIds.reduce(
    (collector, attributeId) => {
      const attribute = objectTypeAttributes?.find(
        ({ id }) => attributeId === id
      );

      const attributeType = attribute?.defaultType?.name || attribute?.type;

      if (!filterableAttributes?.includes(attributeId)) return collector;

      collector.push({
        id: attributeId,
        type: attributeType,
        name: attribute.name,
        categoryName,
        order: collector.length + 1,
      });

      return collector;
    },
    showStockFilter
      ? [
          {
            id: "stock",
            type: "stock",
            name: "Show in-stock items",
            order: 0,
          },
        ]
      : []
  );

  const collectedFilters = filters.reduce((categoryFilterCollector, filter) => {
    if (filter.id === "stock")
      return {
        stock: {
          ...filter,
          options: {
            Yes: assetsValues
              .filter(
                ({ attributes }) =>
                  !layoutAttributes?.stockAttributeId ||
                  getAttributeValues(
                    layoutAttributes?.stockAttributeId,
                    attributes
                  )?.[0] > 0
              )
              .map(({ id }) => id),
          },
        },
        ...categoryFilterCollector,
      };
    const options = assetsValues.reduce(
      (filterOptionsCollector, { attributes, id: assetId }) => {
        getAttributeValues(filter.id, attributes)?.forEach((optionValue) => {
          filterOptionsCollector[optionValue] = [
            ...(filterOptionsCollector?.[optionValue] || []),
            assetId,
          ];
        });
        return filterOptionsCollector;
      },
      {}
    );
    return {
      ...categoryFilterCollector,
      [filter.id]: {
        ...filter,
        options,
      },
    };
  }, {});
  return collectedFilters;
};

export const buildAqlWithPreAndPostFilters = (
  aql,
  ignoreCatalogAql,
  catalogAql,
  objectTypeId
) =>
  [
    typeof ignoreCatalogAql === "undefined" || ignoreCatalogAql
      ? ""
      : catalogAql,
    `ObjectTypeId = ${objectTypeId}`,
    aql,
  ]
    .filter((aqlPart) => !!aqlPart?.trim().length)
    .join(" AND ");

export const getRandomCategoryFromCatalog = (catalog, ignoreCategoryId) => {
  if (!catalog?.categories?.length) return null;
  const randomInteger = Math.floor(Math.random() * catalog.categories.length);
  const pickedCategory = catalog.categories[randomInteger];

  if (pickedCategory.id === ignoreCategoryId)
    return getRandomCategoryFromCatalog(catalog, ignoreCategoryId);

  return catalog.categories[randomInteger];
};
